import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Form, Input, DatePicker, TimePicker, Select, Button, Row, Col, Skeleton, Tooltip, Popconfirm } from 'antd';
import {
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  TeamOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import MainLayout from '../components/MainLayout';
import { calendarService, CalendarEvent, User } from '../services/calendarService';
import { getEmployees } from '../services/employeeService';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PageHeader, SectionCard, EmptyState, PersonAvatar, Pill } from '../components/ui';
import './CalendarPage.css';

const isOrganizer = (user: User) => user.pivot?.type === 'organizer';

const CalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setEvents(await calendarService.getEvents(selectedMonth.month() + 1, selectedMonth.year()));
    } catch (error) {
      console.error('Failed to fetch events', error);
      toast.error(t('calendar.error.loadEvents'));
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, t]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    getEmployees()
      .then(setEmployees)
      .catch((error) => console.error('Failed to fetch employees', error));
  }, []);

  const handleCreate = async (values: any) => {
    try {
      setCreating(true);
      await calendarService.createEvent({
        name: values.name,
        description: values.description,
        date: values.date.format('YYYY-MM-DD'),
        time: values.time.format('HH:mm'),
        user_ids: values.user_ids,
      });
      toast.success(t('calendar.success.createEvent'));
      setCreateModalOpen(false);
      form.resetFields();
      // Show the month the event landed in, so it is visible straight away
      setSelectedMonth(values.date);
      setSelectedDay(values.date.format('YYYY-MM-DD'));
      fetchEvents();
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('calendar.error.createEvent'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (event: CalendarEvent) => {
    try {
      setDeletingId(event.id);
      await calendarService.deleteEvent(event.id);
      toast.success(t('calendar.success.deleteEvent'));
      setSelectedEvent(null);
      fetchEvents();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('calendar.error.deleteEvent'));
    } finally {
      setDeletingId(null);
    }
  };

  // ---------- Calendar ----------
  const byDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((event) => {
      const key = dayjs(event.date).format('YYYY-MM-DD');
      map.set(key, [...(map.get(key) ?? []), event]);
    });
    map.forEach((list) => list.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [events]);

  /** Monday-first grid covering the whole month, padded with the neighbouring days. */
  const weeks = useMemo(() => {
    const first = selectedMonth.startOf('month');
    const start = first.subtract((first.day() + 6) % 7, 'day');
    const result: dayjs.Dayjs[][] = [];
    let cursor = start;
    for (let week = 0; week < 6; week++) {
      const days: dayjs.Dayjs[] = [];
      for (let day = 0; day < 7; day++) {
        days.push(cursor);
        cursor = cursor.add(1, 'day');
      }
      result.push(days);
      if (cursor.isAfter(selectedMonth.endOf('month'))) break;
    }
    return result;
  }, [selectedMonth]);

  const weekdayNames = useMemo(() => {
    const monday = dayjs().startOf('week').add(dayjs().startOf('week').day() === 0 ? 1 : 0, 'day');
    return Array.from({ length: 7 }, (_, index) =>
      monday.add(index, 'day').toDate().toLocaleDateString(i18n.language, { weekday: 'short' }),
    );
  }, [i18n.language]);

  const monthLabel = selectedMonth.toDate().toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  const listedEvents = selectedDay
    ? byDate.get(selectedDay) ?? []
    : [...events].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));

  const seenCount = (event: CalendarEvent) =>
    (event.users ?? []).filter((user) => !isOrganizer(user) && user.pivot?.read_at).length;
  const invitedCount = (event: CalendarEvent) => (event.users ?? []).filter((user) => !isOrganizer(user)).length;

  return (
    <MainLayout bare>
      <PageHeader
        title={t('calendar.title')}
        subtitle={loading ? undefined : t('calendar.eventCount', { count: events.length })}
        actions={
          <>
            <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchEvents} disabled={loading}>
              {t('dashboard.refresh')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
              {t('calendar.newEvent')}
            </Button>
          </>
        }
      />

      <div className="cal-controls e22-card">
        <div className="cal-month">
          <Tooltip title={t('attendance.previousMonth')}>
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() => {
                setSelectedMonth(selectedMonth.subtract(1, 'month'));
                setSelectedDay(null);
              }}
              aria-label={t('attendance.previousMonth')}
            />
          </Tooltip>
          <span className="cal-month-label">{monthLabel}</span>
          <Tooltip title={t('attendance.nextMonth')}>
            <Button
              type="text"
              icon={<RightOutlined />}
              onClick={() => {
                setSelectedMonth(selectedMonth.add(1, 'month'));
                setSelectedDay(null);
              }}
              aria-label={t('attendance.nextMonth')}
            />
          </Tooltip>
        </div>
        {!selectedMonth.isSame(dayjs(), 'month') && (
          <button
            type="button"
            className="e22-link-button"
            onClick={() => {
              setSelectedMonth(dayjs());
              setSelectedDay(null);
            }}
          >
            {t('dashboard.backToThisMonth')}
          </button>
        )}
      </div>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={16}>
          <SectionCard title={monthLabel} icon={<CalendarOutlined />}>
            {loading ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <div className="cal-grid">
                <div className="cal-weekdays">
                  {weekdayNames.map((name) => (
                    <span key={name}>{name}</span>
                  ))}
                </div>
                {weeks.map((week, weekIndex) => (
                  <div className="cal-week" key={weekIndex}>
                    {week.map((day) => {
                      const key = day.format('YYYY-MM-DD');
                      const dayEvents = byDate.get(key) ?? [];
                      const outside = day.month() !== selectedMonth.month();
                      return (
                        <button
                          type="button"
                          key={key}
                          className={[
                            'cal-day',
                            outside ? 'cal-day--outside' : '',
                            day.isSame(dayjs(), 'day') ? 'cal-day--today' : '',
                            selectedDay === key ? 'cal-day--selected' : '',
                          ].join(' ')}
                          onClick={() => setSelectedDay(selectedDay === key ? null : key)}
                        >
                          <span className="cal-day-number">{day.format('D')}</span>
                          <span className="cal-day-items">
                            {dayEvents.slice(0, 3).map((event) => (
                              <span key={event.id} className="cal-chip" title={event.name}>
                                <b>{event.time.substring(0, 5)}</b> {event.name}
                              </span>
                            ))}
                            {dayEvents.length > 3 ? <span className="cal-more">+{dayEvents.length - 3}</span> : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={8}>
          <SectionCard
            title={
              selectedDay
                ? dayjs(selectedDay).toDate().toLocaleDateString(i18n.language, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })
                : monthLabel
            }
            icon={<CalendarOutlined />}
            subtitle={t('calendar.eventCount', { count: listedEvents.length })}
            action={
              selectedDay ? (
                <button type="button" className="e22-link-button" onClick={() => setSelectedDay(null)}>
                  {t('tasks.wholeMonth')}
                </button>
              ) : undefined
            }
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : listedEvents.length === 0 ? (
              <EmptyState
                icon={<CalendarOutlined />}
                text={t('calendar.noEvents')}
                action={
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
                    {t('calendar.newEvent')}
                  </Button>
                }
              />
            ) : (
              <div className="e22-scroll">
                {listedEvents.map((event) => {
                  const invited = invitedCount(event);
                  const seen = seenCount(event);
                  return (
                    <button type="button" key={event.id} className="cal-event" onClick={() => setSelectedEvent(event)}>
                      <span className="cal-event-time">
                        <b>{event.time.substring(0, 5)}</b>
                        <small>
                          {dayjs(event.date).toDate().toLocaleDateString(i18n.language, {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </small>
                      </span>
                      <span className="e22-row-body">
                        <span className="e22-row-title">{event.name}</span>
                        {event.description ? <span className="e22-row-subtitle">{event.description}</span> : null}
                      </span>
                      {invited > 0 ? (
                        <Pill tone={seen === invited ? 'success' : 'neutral'}>
                          {seen === invited ? <CheckCircleOutlined /> : null} {seen}/{invited}
                        </Pill>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </Col>
      </Row>

      {/* Create */}
      <Modal
        title={t('calendar.createEventTitle')}
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={() => form.submit()}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        confirmLoading={creating}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} initialValues={{ date: selectedMonth }}>
          <Form.Item
            name="name"
            label={t('calendar.eventName')}
            rules={[{ required: true, message: t('calendar.validation.eventName') }]}
          >
            <Input placeholder={t('calendar.eventNamePlaceholder')} autoFocus />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="date"
                label={t('calendar.date')}
                rules={[{ required: true, message: t('calendar.validation.date') }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="time"
                label={t('calendar.time')}
                rules={[{ required: true, message: t('calendar.validation.time') }]}
              >
                <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label={t('calendar.description')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="user_ids" label={t('calendar.inviteUsers')} extra={t('calendar.inviteHint')}>
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder={t('calendar.inviteUsersPlaceholder')}
              // Only employees with a user account can be invited — the API takes user ids
              options={employees
                .filter((employee) => employee.user)
                .map((employee) => ({ value: employee.user.id, label: `${employee.name} ${employee.surname}` }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Details */}
      <Modal
        title={t('calendar.eventDetails')}
        open={!!selectedEvent}
        onCancel={() => setSelectedEvent(null)}
        footer={
          selectedEvent ? (
            <div className="cal-details-footer">
              <Popconfirm
                title={t('calendar.deleteConfirm')}
                description={selectedEvent.name}
                onConfirm={() => handleDelete(selectedEvent)}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} loading={deletingId === selectedEvent.id}>
                  {t('common.delete')}
                </Button>
              </Popconfirm>
              <Button onClick={() => setSelectedEvent(null)}>{t('common.close')}</Button>
            </div>
          ) : null
        }
      >
        {selectedEvent && (
          <div className="cal-details">
            <div className="cal-details-head">
              <span className="cal-details-date">
                <b>{dayjs(selectedEvent.date).format('D')}</b>
                <small>
                  {dayjs(selectedEvent.date).toDate().toLocaleDateString(i18n.language, { month: 'short' })}
                </small>
              </span>
              <div className="e22-row-body">
                <div className="cal-details-name">{selectedEvent.name}</div>
                <div className="e22-row-subtitle">
                  {dayjs(selectedEvent.date).toDate().toLocaleDateString(i18n.language, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}{' '}
                  · {selectedEvent.time.substring(0, 5)}
                </div>
              </div>
            </div>

            {selectedEvent.description ? <p className="cal-details-description">{selectedEvent.description}</p> : null}

            <div className="cal-details-section">
              <TeamOutlined /> {t('calendar.invitedUsers')}
              {invitedCount(selectedEvent) > 0 ? (
                <span className="cal-details-seen">
                  {t('calendar.seenOf', { seen: seenCount(selectedEvent), total: invitedCount(selectedEvent) })}
                </span>
              ) : null}
            </div>

            {!selectedEvent.users || selectedEvent.users.length === 0 ? (
              <EmptyState icon={<TeamOutlined />} text={t('calendar.noInvitedUsers')} />
            ) : (
              selectedEvent.users.map((user) => {
                const [first, ...rest] = (user.name ?? '').split(' ');
                return (
                  <div className="e22-row" key={user.id}>
                    <PersonAvatar name={first} surname={rest.join(' ')} size={34} />
                    <div className="e22-row-body">
                      <div className="e22-row-title">{user.name}</div>
                      <div className="e22-row-subtitle">{user.email}</div>
                    </div>
                    {isOrganizer(user) ? (
                      <Pill tone="primary">{t('calendar.organizer')}</Pill>
                    ) : user.pivot?.read_at ? (
                      <Tooltip
                        title={dayjs(user.pivot.read_at.replace(' ', 'T')).format('DD MMM YYYY HH:mm')}
                      >
                        <span>
                          <Pill tone="success">{t('calendar.seen')}</Pill>
                        </span>
                      </Tooltip>
                    ) : (
                      <Pill>{t('calendar.notSeen')}</Pill>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </Modal>
    </MainLayout>
  );
};

export default CalendarPage;
